import { useLayoutEffect, useSyncExternalStore } from 'react'
import type { UserEducationId } from '../userEducationPriority'
import { USER_EDUCATION_PRIORITY } from '../userEducationPriority'

export interface UseIsVisibleUserEducationParams {
  educationId: UserEducationId
  isEligible: boolean
}

export function useIsVisibleUserEducation({
  educationId,
  isEligible,
}: UseIsVisibleUserEducationParams) {
  useLayoutEffect(() => {
    return registerEligibleUserEducation({ educationId, isEligible })
  }, [educationId, isEligible])

  const visibleEducationId = useSyncExternalStore(
    subscribeToUserEducationQueue,
    getVisibleUserEducationId,
    getVisibleUserEducationId,
  )

  return visibleEducationId === educationId
}

const eligibleEducationCounts = new Map<UserEducationId, number>()
const userEducationQueueListeners = new Set<() => void>()
let visibleUserEducationId: UserEducationId | null = null

function registerEligibleUserEducation({
  educationId,
  isEligible,
}: UseIsVisibleUserEducationParams) {
  if (isEligible === false) {
    return
  }

  const nextCount = (eligibleEducationCounts.get(educationId) ?? 0) + 1
  eligibleEducationCounts.set(educationId, nextCount)
  emitUserEducationQueue()

  return () => {
    const remainingCount = (eligibleEducationCounts.get(educationId) ?? 0) - 1
    if (remainingCount <= 0) {
      eligibleEducationCounts.delete(educationId)
    } else {
      eligibleEducationCounts.set(educationId, remainingCount)
    }

    emitUserEducationQueue()
  }
}

function subscribeToUserEducationQueue(listener: () => void) {
  userEducationQueueListeners.add(listener)
  return () => {
    userEducationQueueListeners.delete(listener)
  }
}

function getVisibleUserEducationId() {
  return visibleUserEducationId
}

function emitUserEducationQueue() {
  const nextVisibleUserEducationId =
    USER_EDUCATION_PRIORITY.find((educationId) => {
      return eligibleEducationCounts.has(educationId)
    }) ?? null
  if (nextVisibleUserEducationId === visibleUserEducationId) {
    return
  }

  visibleUserEducationId = nextVisibleUserEducationId
  for (const listener of userEducationQueueListeners) {
    listener()
  }
}
